import React, { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'

import ActionButtonProvider from '../components/action_button/ActionButtonContext'
import CreateActionButton from '../components/action_button/CreateActionButton'
import ConfirmDialog from '../components/dialogs/ConfirmDialog'
import LoadingScreen from '../components/LoadingScreen'
import SearchInput from '../layout/SearchInput'
import DesktopIndexPagination from '../components/DesktopIndexPagination'
import { layoutMobileMediaQuery } from '../layout/layoutConstants'
import { canManagePosTasks } from '../global_context/capabilities/posCapabilities'
import { MobileSearchFilterCard } from '../components/mobile'
import PosTaskDesktopTable from './PosTaskDesktopTable'
import PosTaskMobileList from './PosTaskMobileList'
import PosTaskMobilePagination from './PosTaskMobilePagination'

const MOBILE_SEARCH_DEBOUNCE_MS = 450

const PointOfSaleView = ({
  data = [],
  isFetching,
  title,
  query = '',
  onSearchUpdate,
  onCreate,
  onShow,
  onClone,
  onSuspend,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange
}) => {
  const isMobileList = useMediaQuery(layoutMobileMediaQuery)
  const mobileSearchTimerRef = useRef(null)
  const [searchValue, setSearchValue] = useState(query)
  const [confirmDialog, setConfirmDialog] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState('')

  const canWrite = canManagePosTasks()

  const pagination = useMemo(
    () => ({
      current_page: page,
      per_page: rowsPerPage,
      total_count: totalCount,
      total_pages: Math.ceil((totalCount || 0) / rowsPerPage) || 1
    }),
    [page, rowsPerPage, totalCount]
  )

  useEffect(() => {
    setSearchValue(query)
  }, [query])

  useEffect(
    () => () => {
      clearTimeout(mobileSearchTimerRef.current)
    },
    []
  )

  const submitSearch = (value = searchValue) => {
    clearTimeout(mobileSearchTimerRef.current)
    onSearchUpdate(value)
  }

  const handleSearchChange = (event) => {
    const nextValue = event?.target?.value ?? event
    setSearchValue(nextValue)

    if (isMobileList) {
      clearTimeout(mobileSearchTimerRef.current)
      mobileSearchTimerRef.current = setTimeout(() => {
        onSearchUpdate(nextValue)
      }, MOBILE_SEARCH_DEBOUNCE_MS)
    }
  }

  const handleSearchEnter = (event) => {
    if (event?.key !== 'Enter') return
    event.preventDefault()
    submitSearch()
  }

  const clearSearchInput = () => {
    setSearchValue('')
    submitSearch('')
  }

  const closeConfirmDialog = () => {
    if (!actionLoading) setConfirmDialog(null)
  }

  const confirmDialogAction = async () => {
    const onConfirm = confirmDialog?.onConfirm
    if (!onConfirm) return

    setActionLoading(true)
    try {
      await onConfirm()
      setConfirmDialog(null)
    } catch (error) {
      console.error('Error confirming POS task action:', error)
      setActionError(error?.message || 'No se pudo completar la acción. Intente nuevamente.')
      setConfirmDialog(null)
    } finally {
      setActionLoading(false)
    }
  }

  const requestClone = (model) => {
    setConfirmDialog({
      title: '¿Crear copia de tarea?',
      message: 'Se creará una nueva tarea a partir de esta tarea.',
      confirmLabel: 'Crear copia',
      onConfirm: () => onClone(model.uuid)
    })
  }

  const requestSuspend = (model) => {
    setConfirmDialog({
      title: '¿Suspender tarea?',
      message: '¿Está seguro de que desea continuar con la suspensión de esta tarea?',
      confirmLabel: 'Suspender',
      confirmColor: 'error',
      onConfirm: () => onSuspend(model.uuid)
    })
  }

  if (isFetching) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}
      <div className="index-header detailed-container pos-task-header">
        <h2>{title}</h2>
        {canWrite ? (
          <div className="index-buttons pos-create-action-wrap">
            <CreateActionButton
              text="Crear nueva tarea"
              icon="add_circle"
              iconSize={18}
              onClick={onCreate}
              className="pos-create-task-button"
            />
          </div>
        ) : null}
      </div>

      <div className="index-table pos-task-index-table">
        <div className="show-area pos-task-show-area">
          {isMobileList ? (
            <Box sx={{ mb: 2 }}>
              <MobileSearchFilterCard
                title="Listado de tareas en punto de venta"
                value={searchValue}
                onChange={handleSearchChange}
                onSubmit={() => submitSearch()}
                onClear={clearSearchInput}
                placeholder="Buscar por código o descripción"
              />
            </Box>
          ) : (
            <div className="title-and-search-containter">
              <h4 className="text-sub-title">Listado de tareas en punto de venta:</h4>
              <div className="search-input-containter mb-20">
                <SearchInput
                  query={searchValue}
                  onSearch={submitSearch}
                  onSearchUpdate={handleSearchChange}
                  onClearSearch={clearSearchInput}
                  placeholder="Buscar por código o descripción"
                />
              </div>
            </div>
          )}

          <ActionButtonProvider>
            {isMobileList ? (
              <PosTaskMobileList
                data={data}
                canWrite={canWrite}
                onShow={onShow}
                onRequestClone={requestClone}
                onRequestSuspend={requestSuspend}
              />
            ) : (
              <PosTaskDesktopTable
                data={data}
                canWrite={canWrite}
                onShow={onShow}
                onRequestClone={requestClone}
                onRequestSuspend={requestSuspend}
              />
            )}
          </ActionButtonProvider>

          {isMobileList && pagination.total_pages > 1 ? (
            <Box sx={{ mt: 1 }}>
              <PosTaskMobilePagination
                pagination={pagination}
                onPageChange={(nextPage) => onPageChange(null, nextPage)}
              />
            </Box>
          ) : null}

          {!isMobileList && pagination.total_count > 0 ? (
            <DesktopIndexPagination
              ariaLabel="Paginacion de tareas en punto de venta"
              pagination={pagination}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        confirmColor={confirmDialog?.confirmColor || 'primary'}
        confirmLabel={confirmDialog?.confirmLabel || 'Aceptar'}
        loading={actionLoading}
        message={confirmDialog?.message || ''}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialogAction}
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
      />
    </>
  )
}

export default PointOfSaleView
